import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { SITE } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
    "Terrassement",
    "Démolition & déconstruction",
    "Génie civil, voirie & enrobés",
    "Aménagements extérieurs & enrochement",
    "Autre demande",
];

export default function ContactForm({ compact = false, defaultProjectType }) {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
        projectType: defaultProjectType || "",
        location: "",
        budget: "",
        timeline: "",
        message: "",
        consent: false,
        website: "", // honeypot
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [errors, setErrors] = useState({});

    function update(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
        setErrors((e) => ({ ...e, [field]: undefined }));
    }

    function validate() {
        const e = {};
        if (!form.firstName.trim()) e.firstName = "Prénom requis";
        if (!form.lastName.trim()) e.lastName = "Nom requis";
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email invalide";
        if (!form.phone.trim()) e.phone = "Téléphone requis";
        if (!form.projectType) e.projectType = "Choisissez un type de projet";
        if (!form.consent) e.consent = "Merci d’accepter la Politique de confidentialité avant l’envoi.";
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function handleSubmit(evt) {
        evt.preventDefault();
        if (!validate()) return;
        setStatus("loading");
        try {
            const res = await axios.post(`${API}/leads`, form, { timeout: 30000 });
            if (res.data?.ok) {
                setStatus("success");
                toast.success("Demande envoyée. Nous vous répondons rapidement.");
            } else {
                setStatus("error");
                toast.error("Une erreur est survenue. Merci de réessayer.");
            }
        } catch (err) {
            console.error(err);
            setStatus("error");
            toast.error("Impossible d’envoyer la demande. Réessayez ou appelez-nous au 076 682 30 32 ou 079 644 10 58.");
        }
    }

    if (status === "success") {
        return (
            <div
                data-testid="contact-form-success-message"
                className="rounded-md border border-concrete-200 bg-white p-8 md:p-10 text-center"
            >
                <div className="mx-auto w-14 h-14 rounded-full bg-navy-700 text-white flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="mt-6 font-heading text-2xl md:text-3xl font-semibold text-navy-700">
                    Merci, votre demande a bien été transmise.
                </h3>
                <p className="mt-3 text-navy-700/70 max-w-xl mx-auto">
                    Un membre de l’équipe RCPS Terrassement vous recontacte rapidement afin d’étudier votre projet
                    et vous proposer une solution adaptée.
                </p>
                <button
                    onClick={() => {
                        setStatus("idle");
                        setForm({
                            firstName: "", lastName: "", company: "", email: "", phone: "",
                            projectType: defaultProjectType || "", location: "", budget: "", timeline: "",
                            message: "", consent: false, website: "",
                        });
                    }}
                    className="mt-6 text-navy-700 font-semibold link-underline"
                >
                    Envoyer une nouvelle demande
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            data-testid="contact-quote-form"
            className="rounded-md border border-concrete-200 bg-white p-6 md:p-8"
            noValidate
        >
            {/* Honeypot */}
            <div className="hidden" aria-hidden="true">
                <label>Site web (ne pas remplir)</label>
                <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                />
            </div>

            <div className={`grid gap-5 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}>
                <Field label="Prénom *" error={errors.firstName}>
                    <Input
                        data-testid="contact-input-firstName"
                        value={form.firstName}
                        onChange={(e) => update("firstName", e.target.value)}
                        placeholder="Marie"
                        className="h-11"
                    />
                </Field>
                <Field label="Nom *" error={errors.lastName}>
                    <Input
                        data-testid="contact-input-lastName"
                        value={form.lastName}
                        onChange={(e) => update("lastName", e.target.value)}
                        placeholder="Dupont"
                        className="h-11"
                    />
                </Field>
                <Field label="Email *" error={errors.email}>
                    <Input
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="marie.dupont@example.ch"
                        className="h-11"
                    />
                </Field>
                <Field label="Téléphone *" error={errors.phone}>
                    <Input
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="076 000 00 00"
                        className="h-11"
                    />
                </Field>

                {!compact && (
                    <Field label="Société / Bureau" error={errors.company}>
                        <Input
                            data-testid="contact-input-company"
                            value={form.company}
                            onChange={(e) => update("company", e.target.value)}
                            placeholder="Optionnel"
                            className="h-11"
                        />
                    </Field>
                )}

                <Field label="Type de projet *" error={errors.projectType}>
                    <Select value={form.projectType} onValueChange={(v) => update("projectType", v)}>
                        <SelectTrigger data-testid="contact-input-projectType" className="h-11">
                            <SelectValue placeholder="Choisir une prestation" />
                        </SelectTrigger>
                        <SelectContent>
                            {PROJECT_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>
                                    {t}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>

                {!compact && (
                    <Field label="Localisation du chantier" error={errors.location} full>
                        <Input
                            data-testid="contact-input-location"
                            value={form.location}
                            onChange={(e) => update("location", e.target.value)}
                            placeholder="Ex : Genève, Plan-les-Ouates, Carouge, Nyon, Lausanne…"
                            className="h-11"
                        />
                    </Field>
                )}

                <Field label="Description du projet (facultatif)" error={errors.message} full>
                    <Textarea
                        data-testid="contact-input-message"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        placeholder="Décrivez votre projet, la surface, l’accès, les contraintes, les délais souhaités…"
                        rows={compact ? 3 : 5}
                        className="resize-none"
                    />
                </Field>
            </div>

            <div className="mt-5 flex items-start gap-3">
                <Checkbox
                    id="consent"
                    checked={form.consent}
                    onCheckedChange={(v) => update("consent", Boolean(v))}
                    data-testid="contact-input-consent"
                    className="mt-0.5"
                />
                <Label htmlFor="consent" className="text-xs text-navy-700/80 leading-relaxed cursor-pointer">
                    J’ai lu et j’accepte la{" "}
                    <a
                        href={SITE.legal.privacy}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-navy-700 font-semibold underline underline-offset-2 hover:text-navy-800"
                    >
                        Politique de confidentialité
                    </a>
                    {" "}de RCPS Terrassement.
                </Label>
            </div>
            {errors.consent && (
                <p data-testid="contact-form-error-message" className="mt-2 text-xs text-red-600">
                    {errors.consent}
                </p>
            )}

            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-between">
                <p className="text-xs text-navy-700/60">
                    Réponse après analyse de votre demande · devis personnalisé · sans engagement.
                </p>
                <Button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={status === "loading"}
                    className="h-12 px-6 bg-navy-700 hover:bg-navy-800 text-white font-semibold rounded-md inline-flex items-center gap-2"
                >
                    {status === "loading" ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…
                        </>
                    ) : (
                        <>
                            Envoyer ma demande <Send className="w-4 h-4" />
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
}

function Field({ label, children, error, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="block text-xs font-semibold text-navy-700 mb-1.5 tracking-tight">{label}</label>
            {children}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
}
